// @flow
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { Option } from '@core/types';
import type { SearchCity } from '@shared/Locations/types';

import Button, { buttonConstants } from '@shared/Button';

import AdvancedSelectField from '@shared/AdvancedSelectField';
import SearchField from '@search/components/SearchField';
import DateHelpers from '@helpers/DateHelpers';

import useActivities from '@activity/hooks/useActivities';
import GroupClassesFilter from '../../GroupClass/components/GroupClassesFilter';
import type { GroupClassesFilterType } from '@groupClass/types';
import { GROUP_CLASSES_FILTERS } from '@groupClass/constants';
import clsx from 'clsx';

type Props = {
  activity: Option | null,
  day: Option | null,
  locale: string,
  value: SearchCity | null,
  groupClassesFilter: GroupClassesFilterType,
  onChangeAddress: (event: SyntheticEvent<HTMLInputElement>) => void,
  onChangeDay: () => void,
  onChangeActivity: () => void,
  onChangeGroupClassesFilter: () => void,
  onSubmit: (event: SyntheticEvent<EventTarget>) => void,
};

const SearchVisitor = (props: Props): React$Node => {
  const { t, i18n: { language } } = useTranslation();
  const [ activities, isLoading ] = useActivities(language);

  const containerRef = useRef(null);
  const placeholderRef = useRef(null);
  const [isSticky, setSticky] = useState(false);
  const [height, setHeight] = useState(0);

  const {
    activity,
    day,
    locale,
    value,
    groupClassesFilter,
    onChangeActivity,
    onChangeAddress,
    onChangeDay,
    onChangeGroupClassesFilter,
    onSubmit,
  } = props;

  useEffect(() => {
    const sidebar = containerRef.current;
    if (!sidebar) return;

    let originalOffset = 0;
    let lastScrollY = window.scrollY;

    const onResize = () => {
      if (sidebar) {
        originalOffset = sidebar.getBoundingClientRect().top + window.scrollY;
        setHeight(sidebar.offsetHeight);
      }
    };

    const onScroll = () => {
      const currentScrollY = window.scrollY;
      const scrollingDown = currentScrollY > lastScrollY;
      const scrollingUp = currentScrollY < lastScrollY;

      if (scrollingDown && currentScrollY >= originalOffset + height) {
        setSticky(true);
      }

      if (scrollingUp && currentScrollY <= originalOffset) {
        setSticky(false);
      }

      lastScrollY = currentScrollY;
    };

    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onResize);
    onResize();

    return () => {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onResize);
    };
  }, [height]);

  const activityOptions = useMemo((): Option[] => (
    activities && activities.map((activity) => ({
      label: activity.name,
      value: activity.id.toString(),
    }))
  ), [activities]);

  const daysOptions = DateHelpers.getDaysOfWeekOptions(language);

  return (
    <>
      <div className="find-group-classes">
        <h1 className="find-group-classes-title">{ t('groupClasses.titleVisitor') }</h1>
        <div className="faq-toggle-btn">
          <a key={ t('user.faq') } target="_blank" rel="noreferrer" href={ t('user.faq.link') }>{ t('user.faq') }</a>
        </div>
      </div>
      <GroupClassesFilter
        groupClassesFilter={ groupClassesFilter }
        onChangeGroupClassesFilter={ onChangeGroupClassesFilter }
      />
      { /* Prevent layout shift */ }
      { isSticky && <div ref={ placeholderRef } style={ { height } } /> }
      <div
        ref={ containerRef }
        className={ clsx('search-container', { sticky: isSticky }) }>
        <div className="block-search-city">
          {
            groupClassesFilter !== GROUP_CLASSES_FILTERS.ONLINE && (
              <SearchField value={ value } locale={ locale } isAdmin={ false } onChange={ onChangeAddress } onSearch={ onSubmit } />
            )
          }
          <AdvancedSelectField
            name="activity"
            placeholder={ t('groupClasses.activities') }
            options={ activityOptions }
            isClearable
            className="search-field-input"
            value={ activity }
            onChange={ onChangeActivity }
            label=""
            selectAll={ t('common.allFem') }
            isDisabled={ isLoading }
            isSearchable={ false }
          />
          <AdvancedSelectField
            name="day"
            options={ daysOptions }
            placeholder={ t('groupClasses.groupClass.detailed.day') }
            isClearable
            value={ day }
            className="search-field-input"
            onChange={ onChangeDay }
            label=""
            selectAll={ t('common.allMasc') }
            isSearchable={ false }
          />
        </div>
        <Button type={ buttonConstants.PRIMARY } onClick={ onSubmit }>
          { t('common.search') }
        </Button>
      </div>
    </>
  );
};

export default SearchVisitor;
